import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import {
  FiChevronRight,
  FiHome,
  FiUsers,
  FiSearch,
  FiEdit,
  FiChevronLeft,
  FiDownload,
} from "react-icons/fi";
import toast from "react-hot-toast";

import { getUsers } from "../../store/slices/profile";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const Users = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    users = [],
    usersLoading,
    usersPagination,
    error,
  } = useSelector((state) => state.profile);
  const [search, setSearch] = useState("");
  const [referredBy, setReferredBy] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const totalUsers =
    usersPagination?.totalUsers || 0;

  const totalPages =
    usersPagination?.totalPages || 0;

  const currentPage =
    usersPagination?.currentPage || page;

  const hasNextPage =
    usersPagination?.hasNextPage || false;

  const hasPreviousPage =
    usersPagination?.hasPreviousPage || false;

  useEffect(() => {
    dispatch(
      getUsers({
        search: search.trim(),
        referredBy: referredBy.trim(),
        fromDate,
        toDate,
        page,
        limit,
      })
    );
  }, [
    dispatch,
    search,
    referredBy,
    fromDate,
    toDate,
    page,
    limit,
  ]);


  const handlePrevious = () => {
    if (hasPreviousPage) {
      setPage((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (hasNextPage) {
      setPage((prev) => prev + 1);
    }
  };

  const handlePageChange = (pageNumber) => {
    if (
      pageNumber >= 1 &&
      pageNumber <= totalPages &&
      pageNumber !== page
    ) {
      setPage(pageNumber);
    }
  };

  const handleEdit = (item) => {
    navigate(
      `/dashboard/edit-user-profile/${item._id}`
    );
  };

  const formatDate = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) {
      return date;
    }

    return parsedDate.toLocaleDateString(
      "en-IN",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }
    );
  };
  const handleExportPDF = async () => {
    try {
      const total =
        usersPagination?.totalUsers || 0;

      if (total === 0) {
        toast.error("No users found to export");
        return;
      }
      const response = await dispatch(
        getUsers({
          search: search.trim(),
          referredBy: referredBy.trim(),
          fromDate,
          toDate,
          page: 1,
          limit: total,
        })
      ).unwrap();

      const exportUsers =
        response?.users ||
        response?.data?.users ||
        [];

      if (
        !Array.isArray(exportUsers) ||
        exportUsers.length === 0
      ) {
        toast.error("No users found to export");
        return;
      }

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");

      doc.text(
        "Users Report",
        14,
        15
      );

      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");

      doc.text(
        `Search: ${search.trim() || "All Users"
        }`,
        14,
        22
      );

      doc.text(
        `Referred By: ${referredBy.trim() || "All"
        }`,
        14,
        27
      );

      doc.text(
        `Date Range: ${fromDate || "Any"} to ${toDate || "Any"}`,
        14,
        32
      );

      doc.text(
        `Total Users: ${exportUsers.length}`,
        14,
        37
      );

      doc.text(
        `Exported: ${new Date().toLocaleString(
          "en-IN"
        )}`,
        200,
        22
      );

      const tableData = exportUsers.map(
        (item, index) => {

          const status =
            item.status ||
            item.joiningStatus ||
            "ACTIVE";

          return [
            index + 1,

            item.name || "-",

            item.userId ||
            item.memberId ||
            "-",

            item.referredBy?.name
              ? `${item.referredBy.name}${item.referredBy?.userId
                ? ` (${item.referredBy.userId})`
                : ""
              }`
              : "-",

            item.email || "-",

            item.mobileNo ||
            item.mobile ||
            "-",

            formatDate(
              item.joinedAt ||
              item.createdAt
            ),

            status,
          ];
        }
      );
      autoTable(doc, {
        startY: 43,

        head: [
          [
            "#",
            "Name",
            "User ID",
            "Referred By",
            "Email",
            "Mobile Number",
            "Joined Date",
            "Status",
          ],
        ],

        body: tableData,

        theme: "grid",

        styles: {
          fontSize: 8,
          cellPadding: 3,
          valign: "middle",
        },

        headStyles: {
          fontSize: 8,
          fontStyle: "bold",
          halign: "center",
        },

        bodyStyles: {
          fontSize: 8,
        },

        didDrawPage: (data) => {
          const pageCount =
            doc.internal.getNumberOfPages();

          doc.setFontSize(8);

          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            14,
            doc.internal.pageSize.height - 8
          );

          doc.text(
            "Users Report",
            doc.internal.pageSize.width - 45,
            doc.internal.pageSize.height - 8
          );
        },
      });

      const fileName =
        search.trim() ||
          referredBy.trim() ||
          fromDate ||
          toDate
          ? "filtered-users-report.pdf"
          : "all-users-report.pdf";

      doc.save(fileName);

      toast.success(
        `${exportUsers.length} users exported successfully`
      );
      await dispatch(
        getUsers({
          search: search.trim(),
          referredBy: referredBy.trim(),
          fromDate,
          toDate,
          page,
          limit,
        })
      );

    } catch (error) {
      console.error(
        "EXPORT USERS ERROR:",
        error
      );

      toast.error(
        typeof error === "string"
          ? error
          : error?.message ||
          "Failed to export users"
      );
    }
  };
  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      for (
        let i = 1;
        i <= totalPages;
        i++
      ) {
        pages.push(i);
      }
    } else {
      if (page <= 3) {
        pages.push(
          1,
          2,
          3,
          4,
          "...",
          totalPages
        );
      } else if (
        page >= totalPages - 2
      ) {
        pages.push(
          1,
          "...",
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          page - 1,
          page,
          page + 1,
          "...",
          totalPages
        );
      }
    }

    return pages;
  };

  const displayStart =
    totalUsers > 0
      ? (page - 1) * limit + 1
      : 0;

  const displayEnd = Math.min(
    page * limit,
    totalUsers
  );

  return (
    <div className="gi-referral-page">

      <div className="gi-breadcrumb">

        <Link to="/dashboard">
          <FiHome />
          Dashboard
        </Link>

        <FiChevronRight
          className="gi-breadcrumb-arrow"
        />

        <span>Users</span>

      </div>

      <section className="gi-referral-table-card">

        <div className="gi-users-filter">
          {/* DATA PER PAGE */}
          <div className="gi-users-page-size me-2">

            <label htmlFor="pageSize">
              Show
            </label>

            <select
              id="pageSize"
              value={limit}
              onChange={(e) => {
                setLimit(
                  Number(e.target.value)
                );
                setPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>

            <span>
              per page
            </span>

          </div>


          {/* SEARCH */}
          <div className="gi-users-search me-2">

            <FiSearch />

            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search by name, user ID, email or mobile..."
            />

          </div>


          {/* REFERRED BY */}
          <div className="gi-users-search me-2">

            <FiUsers />

            <input
              type="text"
              value={referredBy}
              onChange={(e) => {
                setReferredBy(e.target.value);
                setPage(1);
              }}
              placeholder="Search by referred user ID..."
            />

          </div>



          {/* FROM DATE */}
          <div className="gi-users-search me-2">
            <label htmlFor="usersFromDate" className="me-2">
              From
            </label>
            <input
              id="usersFromDate"
              type="date"
              value={fromDate}
              max={toDate || undefined}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
              aria-label="From date"
            />
          </div>

          {/* TO DATE */}
          <div className="gi-users-search me-2">
            <label htmlFor="usersToDate" className="me-2">
              To
            </label>
            <input
              id="usersToDate"
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(1);
              }}
              aria-label="To date"
            />
          </div>

          {/* CLEAR DATE FILTERS */}
          {(fromDate || toDate) && (
            <button
              type="button"
              className="gi-user-export-btn me-2"
              onClick={() => {
                setFromDate("");
                setToDate("");
                setPage(1);
              }}
            >
              Clear Dates
            </button>
          )}

          {/* EXPORT */}
          <button
            type="button"
            className="gi-user-export-btn"
            onClick={handleExportPDF}
            disabled={
              usersLoading ||
              users.length === 0
            }
          >
            <FiDownload />

            <span>
              Export PDF
            </span>
          </button>

        </div>
        <div className="gi-referral-table-wrapper">

          <table className="gi-referral-table">

            <thead>

              <tr>

                <th>#</th>

                <th className="text-center">
                  Name
                </th>

                <th className="text-center">
                  User ID
                </th>

                <th className="text-center">
                  Referred By
                </th>

                <th className="text-center">
                  Email
                </th>

                <th className="text-center">
                  Mobile Number
                </th>
                <th className="text-center">
                  Password
                </th>

                <th className="text-center">
                  Joined Date
                </th>

                <th className="text-center">
                  Status
                </th>

                <th className="text-center">
                  Action
                </th>

              </tr>

            </thead>

            <tbody>


              {usersLoading &&
                Array.from({
                  length: limit,
                }).map(
                  (_, index) => (
                    <tr
                      key={`skeleton-${index}`}
                    >

                      <td>
                        <Skeleton width={25} />
                      </td>

                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems:
                              "center",
                            justifyContent:
                              "center",
                            gap: "10px",
                          }}
                        >
                          <Skeleton
                            circle
                            width={34}
                            height={34}
                          />

                          <Skeleton
                            width={90}
                          />
                        </div>
                      </td>

                      <td className="text-center">
                        <Skeleton width={75} />
                      </td>

                      <td className="text-center">
                        <Skeleton width={100} />
                      </td>

                      <td className="text-center">
                        <Skeleton width={140} />
                      </td>

                      <td className="text-center">
                        <Skeleton width={100} />
                      </td>

                      <td className="text-center">
                        <Skeleton width={90} />
                      </td>

                      <td className="text-center">
                        <Skeleton
                          width={70}
                          height={25}
                          borderRadius={20}
                        />
                      </td>

                      <td className="text-center">
                        <Skeleton
                          width={65}
                          height={32}
                          borderRadius={8}
                        />
                      </td>

                    </tr>
                  )
                )}

              {!usersLoading &&
                error && (
                  <tr>

                    <td
                      colSpan="9"
                      className="gi-table-message"
                    >
                      {error}
                    </td>

                  </tr>
                )}


              {!usersLoading &&
                !error &&
                users.length === 0 && (
                  <tr>

                    <td
                      colSpan="9"
                      className="gi-table-message"
                    >
                      No users found
                    </td>

                  </tr>
                )}


              {!usersLoading &&
                !error &&
                users.map(
                  (item, index) => {

                    const status =
                      item.status ||
                      item.joiningStatus ||
                      "ACTIVE";

                    const serialNumber =
                      (page - 1) *
                      limit +
                      index +
                      1;

                    return (
                      <tr
                        key={
                          item._id ||
                          item.userId ||
                          index
                        }
                      >

                        {/* NUMBER */}

                        <td className="gi-referral-number">
                          {String(
                            serialNumber
                          ).padStart(
                            2,
                            "0"
                          )}
                        </td>

                        {/* NAME */}

                        <td>

                          <div>
                            {item.name ||
                              "-"}
                          </div>

                        </td>

                        {/* USER ID */}

                        <td className="text-center">

                          <span className="gi-user-id">
                            {item.userId ||
                              item.memberId ||
                              "-"}
                          </span>

                        </td>

                        {/* REFERRED BY */}

                        <td>

                          <b>
                            {item
                              .referredBy
                              ?.name ||
                              "-"}
                          </b>

                          <div>
                            {item
                              .referredBy
                              ?.userId ||
                              "-"}
                          </div>

                        </td>

                        {/* EMAIL */}

                        <td className="text-center">

                          <span className="gi-email-text">
                            {item.email ||
                              "-"}
                          </span>

                        </td>

                        {/* MOBILE */}

                        <td className="text-center">

                          <span className="gi-mobile-text">
                            {item.mobileNo ||
                              item.mobile ||
                              "-"}
                          </span>

                        </td>
                        <td className="text-center">

                          <span

                          >
                            {item?.password}
                          </span>

                        </td>

                        {/* JOINED DATE */}

                        <td className="text-center">

                          <span className="gi-date-text">
                            {formatDate(
                              item.joinedAt ||
                              item.createdAt
                            )}
                          </span>

                        </td>

                        {/* STATUS */}

                        <td className="text-center">

                          <span
                            className={`gi-status-badge ${status.toLowerCase()}`}
                          >
                            {status}
                          </span>

                        </td>

                        {/* ACTION */}

                        <td className="text-center">

                          <button
                            type="button"
                            className="gi-user-edit-btn"
                            onClick={() =>
                              handleEdit(item)
                            }
                            title="Edit User"
                          >

                            <FiEdit />

                            <span>
                              Edit
                            </span>

                          </button>

                        </td>

                      </tr>
                    );
                  }
                )}

            </tbody>

          </table>

        </div>


        {!usersLoading &&
          !error &&
          totalPages > 0 && (

            <div className="gi-users-pagination">

              {/* PAGINATION INFO */}

              <div className="gi-pagination-info">

                Showing{" "}

                <strong>
                  {displayStart}
                </strong>

                {" "}to{" "}

                <strong>
                  {displayEnd}
                </strong>

                {" "}of{" "}

                <strong>
                  {totalUsers}
                </strong>

                {" "}users

              </div>

              {/* PAGINATION BUTTONS */}

              <div className="gi-pagination-buttons">

                {/* PREVIOUS */}

                <button
                  type="button"
                  className="gi-pagination-btn"
                  onClick={
                    handlePrevious
                  }
                  disabled={
                    !hasPreviousPage
                  }
                >

                  <FiChevronLeft />

                  <span>
                    Prev
                  </span>

                </button>

                {/* PAGE NUMBERS */}

                {getPageNumbers().map(
                  (
                    pageNumber,
                    index
                  ) => {

                    if (
                      pageNumber ===
                      "..."
                    ) {
                      return (
                        <span
                          key={`dots-${index}`}
                          className="gi-pagination-dots"
                        >
                          ...
                        </span>
                      );
                    }

                    return (
                      <button
                        type="button"
                        key={pageNumber}
                        className={`gi-pagination-number ${page ===
                          pageNumber
                          ? "active"
                          : ""
                          }`}
                        onClick={() =>
                          handlePageChange(
                            pageNumber
                          )
                        }
                      >
                        {pageNumber}
                      </button>
                    );
                  }
                )}

                {/* NEXT */}

                <button
                  type="button"
                  className="gi-pagination-btn"
                  onClick={
                    handleNext
                  }
                  disabled={
                    !hasNextPage
                  }
                >

                  <span>
                    Next
                  </span>

                  <FiChevronRight />

                </button>

              </div>

            </div>

          )}

      </section>

    </div>
  );
};

export default Users;
